import axios from 'axios';
import { saveAs } from 'file-saver';

export const getTwelveBankAccountsApi = async () => {
  const response = await axios.get('/admin/deal/bank-account');
  return response.data;
};

export const getCustomersDealsOverviewApi = async ({ revenueRecognition }) => {
  const response = await axios.get('/admin/deal/customer', {
    params: {
      revenueRecognition,
    },
  });
  return response.data;
};

export const getCashFlowFeeExposureApi = async ({ tenant, recalc = false }) => {
  const response = await axios.get('/admin/deal/cashflow-fee-exposure', {
    params: {
      tenant,
      ...(recalc ? { recalc: 'true' } : {}),
    },
  });
  return response.data;
};

export const upsertPipelineExposureClientApi = async (customerId, data) => {
  const response = await axios.put(
    `/admin/deal/cashflow-fee-exposure/pipeline-clients/${customerId}`,
    data
  );
  return response.data;
};

export const upsertPipelineExposureSettingsApi = async (data) => {
  const response = await axios.put(
    '/admin/deal/cashflow-fee-exposure/pipeline-settings',
    data
  );
  return response.data;
};

export const savePipelineExposureBatchApi = async (data) => {
  const response = await axios.put(
    '/admin/deal/cashflow-fee-exposure/pipeline-batch',
    data
  );
  return response.data;
};

export const listSourcesUsesScenariosApi = async ({ tenant }) => {
  const response = await axios.get('/admin/deal/cashflow-fee-exposure/scenarios', {
    params: { tenant },
  });
  return response.data;
};

export const getSourcesUsesScenarioApi = async ({ tenant, scenarioId }) => {
  const response = await axios.get(
    `/admin/deal/cashflow-fee-exposure/scenarios/${encodeURIComponent(scenarioId)}`,
    { params: { tenant } }
  );
  return response.data;
};

const scenarioPath = (scenarioId) => (
  `/admin/deal/cashflow-fee-exposure/scenarios/${encodeURIComponent(scenarioId)}`
);

// Create a scenario from the Save & Run that just finished.
export const createSourcesUsesScenarioApi = async (data) => {
  const response = await axios.post('/admin/deal/cashflow-fee-exposure/scenarios', data);
  return response.data;
};

// Overwrite an open (not finished) scenario from the Save & Run that just finished.
export const overwriteSourcesUsesScenarioApi = async ({ scenarioId, ...data }) => {
  const response = await axios.put(scenarioPath(scenarioId), data);
  return response.data;
};

export const freezeSourcesUsesScenarioApi = async ({ tenant, scenarioId }) => {
  const response = await axios.post(`${scenarioPath(scenarioId)}/freeze`, null, { params: { tenant } });
  return response.data;
};

export const deleteSourcesUsesScenarioApi = async ({ tenant, scenarioId }) => {
  const response = await axios.delete(scenarioPath(scenarioId), { params: { tenant } });
  return response.data;
};

export const getSourcesUsesCompanyCashApi = async ({ tenant }) => {
  const response = await axios.get('/admin/deal/cashflow-fee-exposure/company-cash', {
    params: { tenant },
  });
  return response.data;
};

// Saves Main's company cash, and the open scenario's too when scenarioId is given.
export const saveSourcesUsesCompanyCashApi = async ({ tenant, scenarioId, inputs }) => {
  const url = scenarioId
    ? `${scenarioPath(scenarioId)}/company-cash`
    : '/admin/deal/cashflow-fee-exposure/company-cash';
  const response = await axios.put(url, { tenant, inputs });
  return response.data;
};

export const getCustomerInstallmentsOverviewApi = async ({
  customerId,
  revenueRecognition,
}) => {
  const response = await axios.get(
    `/admin/deal/customer/${customerId}/installments`,
    {
      params: {
        revenueRecognition,
      },
    }
  );
  return response.data;
};

export const getInstallmentApi = async ({ customerId, installmentId }) => {
  const response = await axios.get(
    `/admin/deal/customer/${customerId}/installment/${installmentId}`
  );
  return response.data;
};

export const createInstallmentApi = async ({ customerId, data }) => {
  const response = await axios.post(
    `/admin/deal/customer/${customerId}/installment`,
    data
  );
  return response.data;
};

export const updateInstallmentApi = async ({
  customerId,
  installmentId,
  data,
}) => {
  const response = await axios.put(
    `/admin/deal/customer/${customerId}/installment/${installmentId}`,
    data
  );
  return installmentId; // to make it compatible with createInstallmentApi 
};

export const deleteInstallmentApi = async ({ customerId, installmentId }) => {
  const response = await axios.delete(
    `/admin/deal/customer/${customerId}/installment/${installmentId}`
  );
  return response.data;
};

export const deleteRepaymentApi = async ({ customerId, repaymentId }) => {
  const response = await axios.delete(
    `/admin/deal/customer/${customerId}/repayment/${repaymentId}`
  );
  return response.data;
};


// export const setInstallmentAsFundedApi = async ({
//   customerId,
//   installmentId,
// }) => {
//   const response = await axios.put(
//     `/admin/deal/customer/${customerId}/installment/${installmentId}/set-as-funded`
//   );
//   return response.data;
// };

export const getRemainingInstallmentsApi = async ({ customerId }) => {
  const response = await axios.get(
    `/admin/deal/customer/${customerId}/remaining-installments`
  );
  return response.data;
};

export const createRepaymentAllocationApi = async ({
  customerId,
  bankAccountId,
  transferDate,
  allocations,
  setOffAmount,
  facilityFeeId,
  invoiceId,
}) => {
  const response = await axios.post(
    `/admin/deal/customer/${customerId}/repayment-allocation`,
    {
      bankAccountId,
      transferDate,
      allocations,
      setOffAmount,
      facilityFeeId,
      invoiceId,
    }
  );
  return response.data;
};

export const getInstallmentRepaymentsApi = async ({
  customerId,
  installmentId,
  revenueRecognition,
}) => {
  const response = await axios.get(
    `/admin/deal/customer/${customerId}/installment/${installmentId}/repayments`,
    {
      params: {
        revenueRecognition,
      },
    }
  );
  return response.data;
};

export const updateCascadeAssetTapeReportApi = async () => {
  const response = await axios.get(
    '/admin/deal/update-cascade-asset-tape-report'
  );
  return response.data;
};

export const getDealsApi = async ({ customerId }) => {
  const response = await axios.get(`/admin/deal/customer/${customerId}/deals`);
  return response.data;
};

export const createDealApi = async ({ customerId, data }) => {
  const response = await axios.post(
    `/admin/deal/customer/${customerId}/deal`,
    data
  );
  return response.data;
};

export const updateDealApi = async ({ customerId, dealId, data }) => {
  const response = await axios.put(
    `/admin/deal/customer/${customerId}/deal/${dealId}`,
    data
  );
  return response.data;
};

export const getDealApi = async ({ customerId, dealId }) => {
  const response = await axios.get(
    `/admin/deal/customer/${customerId}/deal/${dealId}`
  );
  return response.data;
};

export const canDeleteDealApi = async ({ customerId, dealId }) => {
  const response = await axios.get(
    `/admin/deal/customer/${customerId}/deal/${dealId}/can-delete`
  );
  return response.data;
};

export const deleteDealApi = async ({ customerId, dealId }) => {
  const response = await axios.delete(
    `/admin/deal/customer/${customerId}/deal/${dealId}`
  );
  return response.data;
};

export const getAmortizationApi = async ({ customerId, installmentId, refreshData }) => {
  const response = await axios.get(
    `/admin/deal/customer/${customerId}/amortization?installmentId=${installmentId??''}`,
    {
      params: {
        refreshData,
      },
      timeout: refreshData ? 300000 : 60000,
    }
  );
  return response.data;
};

export const calcInstallmentAmortizationApi = async ({
  customerId,
  dealType,
  purchasePrice,
  feeSettings,
  repaymentFrequency,
  repaymentDays,
  daysBeforeEndOfMonth,
  numberOfRepayments,
  firstRepaymentDate,
  cohortStartDate,
  cohortEndDate,
  pctFromRevenueShare,
  revenueTotalType,
  isTrueUp,
  transferDate,
}) => {
  const response = await axios.post(
    `/admin/deal/customer/${customerId}/calc-installment-amortization`,
    {
      dealType,
      purchasePrice,
      feeSettings,
      repaymentFrequency,
      repaymentDays,
      daysBeforeEndOfMonth,
      numberOfRepayments,
      firstRepaymentDate,
      cohortStartDate,
      cohortEndDate,
      pctFromRevenueShare,
      revenueTotalType,
      isTrueUp,
      transferDate,
    }
  );
  return response.data;
};

export const downloadInstallmentsReportApi = async ({ fileName, tenant }) => {
  const params = {};
  if (tenant && tenant !== 'Combined') {
    params.tenant = tenant;
  }
  const response = await axios.get(`admin/deal/installment-report`, {
    responseType: 'blob',
    params,
  });

  const blob = new Blob([response.data], { type: response.data.type });
  saveAs(blob, fileName);
};

export const downloadCustomerMonthlyStatementApi = async ({
  customerId,
  fileName,
  downloadDate,
}) => {
  const response = await axios.get(
    `admin/deal/customer/${customerId}/repayments-report`,
    {
      responseType: 'blob',
      params: {
        downloadDate,
      },
    }
  );

  const blob = new Blob([response.data], { type: response.data.type });
  saveAs(blob, fileName);
};

export const deleteCustomerForAssetTapeApi = async (customerId) => {
  const response = await axios.delete(`/admin/deal/customer/${customerId}`);
  return response.data;
};
